/**
 * Public shop-info client.
 *
 * Hits `/api/v1/public/shop-info?slug={domain}` which is unauthenticated
 * by design — the SPA uses it before login to render the merchant's name
 * + logo on every auth page.
 *
 * Wave 8 Task 4: backend `_resolve_shop` now also matches `portal_slug`,
 * so passing the new Wave-8 slug (e.g. "kernvel-vendoriq" without
 * .myshopify.com) Just Works. Response gained `welcome_copy`,
 * `support_email`, and `portal_slug` for white-label pages to surface.
 */
import apiClient, { setAccessToken, setNativeSessionScope } from './client';
import { beginSessionTransition, serializeSessionRequest, sessionIsCurrent } from './authSession';

export interface PublicShopInfo {
  found: boolean;
  shopify_domain: string;
  shop_name: string | null;
  portal_name: string;
  logo_url: string;
  primary_color: string;
  login_url: string;
  register_url: string;
  // Wave 8 Task 4 additions — empty strings on free-tier shops
  welcome_copy?: string;
  support_email?: string;
  portal_slug?: string;
}

export const shopApi = {
  async getInfo(slug: string): Promise<PublicShopInfo> {
    // axios baseURL is `/api/v1` so the relative path is `/public/shop-info`
    const res = await apiClient.get<{ success: boolean; data: PublicShopInfo }>(
      '/public/shop-info',
      { params: { slug } },
    );
    return res.data.data;
  },

  /**
   * Wave 8 Task 4: redeem an admin-issued demo impersonation token.
   *
   * Called by the DemoSSO page (route `/<slug>/auth/demo-sso`). The
   * admin's "Open demo vendor portal" button on the Remix admin opens
   * a URL like:
   *   vendors.kernvellabs.com/<slug>/auth/demo-sso?token=...&refresh=...
   *
   * The page extracts query params, calls this method, and on success
   * the backend has set our standard auth cookies — so we then call
   * /vendor/auth/me to populate AuthContext and redirect to /dashboard.
   */
  async redeemDemoSSO(token: string, refresh?: string): Promise<{
    user: any;
    tokens: {
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
    };
    is_demo_session: boolean;
  }> {
    const owner = beginSessionTransition();
    setAccessToken(null);
    setNativeSessionScope(null);
    const res = await serializeSessionRequest(owner, () => apiClient.post<{
      success: boolean;
      data: {
        user: any;
        tokens: any;
        is_demo_session: boolean;
      };
    }>('/vendor/auth/demo-sso', { token, refresh_token: refresh }));
    if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
    setAccessToken(res.data.data.tokens.access_token);
    if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
    setNativeSessionScope((res.data.data as any).native_session_scope ?? null);
    return res.data.data;
  },

  /**
   * Admin-as-vendor ("Manage as vendor"): redeem the impersonation token a
   * merchant admin generated to manage a REAL vendor's store on their behalf.
   *
   * Called by the AdminSSO page (route `/<slug>/auth/admin-sso`). Unlike the
   * demo flow, the redeemed session is MARKED as impersonated server-side:
   * sensitive endpoints (payout/KYB/identity) are blocked, every catalog change
   * is logged to the vendor's activity timeline, and the vendor is notified.
   */
  async redeemAdminSSO(token: string, refresh?: string): Promise<{
    user: any;
    tokens: {
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
    };
    is_impersonated_session: boolean;
    impersonation: { actor?: string; reason?: string | null; started?: string } | null;
  }> {
    const owner = beginSessionTransition();
    setAccessToken(null);
    setNativeSessionScope(null);
    const res = await serializeSessionRequest(owner, () => apiClient.post<{ success: boolean; data: any }>(
      '/vendor/auth/admin-sso',
      { token, refresh_token: refresh },
    ));
    if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
    setAccessToken(res.data.data.tokens.access_token);
    if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
    setNativeSessionScope(res.data.data.native_session_scope ?? null);
    return res.data.data;
  },
};
